import React from 'react';
import { Box, Button, Card, CardContent, Container, Grid, Stack, Typography } from '@mui/material';
import { ArrowForward, AutoAwesome, Hub, Insights } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Clients from '../components/Clients';
import products from './ProductData';
import contentData from './ContentData';
import heroImage from '../assets/banner-background.jpg';
import aiImage from '../assets/AI-service-image.jpg';
import softwareImage from '../assets/sdlc-service-image.jpg';
import dataImage from '../assets/data-service-image.jpg';
import skylarkReadsLogo from '../assets/skylark-reads-logo.jpg';

const serviceHighlights = [
  {
    title: 'AI-Driven Solutions',
    description: 'Explore how our AI and machine learning solutions drive efficiency, innovation, and intelligent decision-making across industries.',
    image: aiImage,
  },
  {
    title: 'Software Development',
    description: 'We create custom software solutions designed to meet the specific challenges and objectives of your business.',
    image: softwareImage,
  },
  {
    title: 'Data Analytics & Insights',
    description: 'Unlock the power of data with our analytics services, delivering insights that inform decision-making and business growth.',
    image: dataImage,
  },
];

const strengths = [
  { icon: <AutoAwesome />, title: 'Innovation' },
  { icon: <Hub />, title: 'Collaboration' },
  { icon: <Insights />, title: 'Expertise' },
];

const Home = () => {
  const product = products[0];

  return (
    <Box>
      <Box
        component="section"
        sx={{
          minHeight: { xs: 'auto', md: 660 },
          display: 'flex',
          alignItems: 'center',
          background:
            'radial-gradient(circle at 78% 18%, rgba(25, 167, 206, 0.2), transparent 28%), linear-gradient(135deg, #f7fbff 0%, #eaf4fb 54%, #ffffff 100%)',
          overflow: 'hidden',
          pt: { xs: 5, md: 6 },
          pb: { xs: 7, md: 7 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  color: 'primary.main',
                  fontSize: { xs: '2.35rem', md: '3.6rem' },
                  lineHeight: 1.12,
                  maxWidth: 620,
                }}
              >
                Crafting Tomorrow’s Solutions with Today’s Expertise
              </Typography>
              <Typography sx={{ color: 'text.secondary', mt: 3, maxWidth: 620, fontSize: '1.08rem', lineHeight: 1.8 }}>
                {contentData.whoAreWe.description}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }}>
                <Button component={Link} to="/services" variant="contained" size="large" endIcon={<ArrowForward />}>
                  Explore Services
                </Button>
                <Button component={Link} to={product.path} variant="outlined" size="large">
                  View {product.name}
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  height: { xs: 340, md: 460 },
                  boxShadow: '0 24px 70px rgba(15, 47, 95, 0.16)',
                }}
              >
                <Box component="img" src={heroImage} alt="Epytome IT digital solutions" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(180deg, rgba(15,47,95,0.08), rgba(15,47,95,0.5))',
                  }}
                />
                <Card sx={{ position: 'absolute', left: 24, right: 24, bottom: 24, background: 'rgba(255,255,255,0.94)', backdropFilter: 'blur(16px)', boxShadow: '0 18px 44px rgba(11,23,39,0.18)' }}>
                  <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box
                        component="img"
                        src={skylarkReadsLogo}
                        alt="Skylark Reads logo"
                        sx={{ width: 58, height: 58, objectFit: 'contain', borderRadius: 2, flexShrink: 0 }}
                      />
                      <Box>
                        <Typography variant="h6">{product.name}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>{product.tagline}</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }}>
        <Grid container spacing={3}>
          {strengths.map((item) => (
            <Grid item xs={12} md={4} key={item.title}>
              <Card sx={{ height: '100%', border: '1px solid rgba(15,47,95,0.08)', boxShadow: '0 18px 45px rgba(16,32,51,0.06)' }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ color: 'secondary.main', mb: 2 }}>{item.icon}</Box>
                  <Typography variant="h5">{item.title}</Typography>
                  <Typography sx={{ mt: 2, color: 'text.secondary', lineHeight: 1.75 }}>
                    {contentData.whatSetsUsApart.find((entry) => entry.title === item.title)?.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box component="section" sx={{ backgroundColor: '#ffffff', py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'end' }} spacing={3} sx={{ mb: 4 }}>
            <Box>
              <Typography variant="h3">Services built for intelligent growth</Typography>
              <Typography sx={{ color: 'text.secondary', mt: 2, maxWidth: 680 }}>
                Unlock your business's full potential with our comprehensive range of cutting-edge services.
              </Typography>
            </Box>
            <Button component={Link} to="/services" endIcon={<ArrowForward />}>All Services</Button>
          </Stack>
          <Grid container spacing={3}>
            {serviceHighlights.map((service) => (
              <Grid item xs={12} md={4} key={service.title}>
                <Card sx={{ height: '100%', overflow: 'hidden', border: '1px solid rgba(15,47,95,0.08)', boxShadow: 'none' }}>
                  <Box component="img" src={service.image} alt={service.title} sx={{ height: 220, width: '100%', objectFit: 'cover' }} />
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h5">{service.title}</Typography>
                    <Typography sx={{ color: 'text.secondary', mt: 1.5, lineHeight: 1.7 }}>{service.description}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 7, md: 10 }, background: 'linear-gradient(135deg, #0b1727 0%, #0f2f5f 100%)', color: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3">{product.name}</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.78)', mt: 2, lineHeight: 1.8 }}>{product.description}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 2, background: 'rgba(255,255,255,0.08)', color: 'white', border: '1px solid rgba(255,255,255,0.16)', boxShadow: 'none' }}>
                <CardContent>
                  <Box
                    component="img"
                    src={skylarkReadsLogo}
                    alt="Skylark Reads logo"
                    sx={{ width: 120, height: 'auto', display: 'block', mb: 3, borderRadius: 2, backgroundColor: 'white' }}
                  />
                  <Typography variant="h5">{product.tagline}</Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
                    <Button component={Link} to={product.path} variant="contained" color="secondary">
                      Learn More
                    </Button>
                    <Button component="a" href={product.externalUrl} target="_blank" rel="noreferrer" variant="outlined" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.38)' }}>
                      Visit Site
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Clients />
    </Box>
  );
};

export default Home;
